import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@core/providers/http';
import { PapelEfetivoEscalaEnum } from '@core/services/domain-services';
import { TipoServicoContrato } from '@core/models/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface TipoServicoOption {
  value: TipoServicoContrato;
  label: string;
}

export interface EnumOption<T extends string = string> {
  /** Chave numérica devolvida pela API ("1", "2", ...). */
  key: string;
  /** Valor canónico enviado nos bodies: uppercase, sem acentos e com _. */
  value: T;
  /** Label de exibição tal como vem da API. */
  label: string;
}

@Injectable({ providedIn: 'root' })
export class EnumService {
  constructor(private readonly apiService: ApiService) {}

  getCategoriaEfetivo(): Observable<any> {
    return this.apiService.get('/api/Enum/CategoriaEfetivo', this.getParams());
  }

  getFuncaoEfetivo(): Observable<any> {
    return this.apiService.get('/api/Enum/FuncaoEfetivo', this.getParams());
  }

  getGenero(): Observable<any> {
    return this.apiService.get('/api/Enum/Genero', this.getParams());
  }

  getParentesco(): Observable<any> {
    return this.apiService.get('/api/Enum/Parentesco', this.getParams());
  }

  getOcorrenciaCriticidade(): Observable<any> {
    return this.apiService.get(
      '/api/Enum/OcorrenciaCriticidade',
      this.getParams(),
    );
  }

  getTipoOcorrencia(): Observable<any> {
    return this.apiService.get('/api/Enum/TipoOcorrencia', this.getParams());
  }

  getEstadoEfetivo(): Observable<any> {
    return this.apiService.get('/api/Enum/EstadoEfetivo', this.getParams());
  }

  getEstadoEquipamento(): Observable<any> {
    return this.apiService.get('/api/Enum/EstadoEquipamento', this.getParams());
  }

  getPapelEfetivoEscalaEnum(): Observable<any> {
    return this.apiService.get(
      '/api/Enum/PapelEfetivoEscala',
      this.getParams(),
    );
  }

  /** GET /api/Enum/{enumName} → { "1": "EFetivo", ... }. */
  getEnum(enumName: string): Observable<Record<string, string>> {
    return this.apiService
      .get<any>(`/api/Enum/${enumName}`)
      .pipe(map((response) => response?.data ?? response ?? {}));
  }

  getEnumOptions<T extends string = string>(
    enumName: string,
  ): Observable<EnumOption<T>[]> {
    return this.getEnum(enumName).pipe(
      map((record) =>
        Object.entries(record).map(([key, label]) => ({
          key,
          label,
          value: this.toEnumValue(label) as T,
        })),
      ),
    );
  }

  getPapelEfetivoEscala(): Observable<EnumOption<string>[]> {
    return this.getEnumOptions<string>('PapelEfetivoEscala');
  }

  getTipoServico(): Observable<TipoServicoOption[]> {
    return this.getEnum('TipoServico').pipe(
      map((record) =>
        Object.entries(record).map(([key, label]) => ({
          value: Number(key) as TipoServicoContrato,
          label,
        })),
      ),
    );
  }

  private getParams(
    params?: Record<string, string | number | boolean>,
  ): HttpParams {
    return params ? new HttpParams({ fromObject: params }) : new HttpParams();
  }

  private toEnumValue(label: string): string {
    return label
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .toUpperCase();
  }
}
